import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

async function ExportToExcel({
    data,
    branches,
    branchId,
    filter,
    filterDateStart,
    filterDateEnd,
    snType,
    t,
}: {
    data: any[]
    branches: any[]
    branchId: number
    filter: any
    filterDateStart: string
    filterDateEnd: string
    snType: boolean
    t: any
}) {
    try {
        if (!Array.isArray(data) || data.length === 0) {
            alert(t('REPORT.TITLE.NO_DATA'));
            return;
        }
        const workbook = new ExcelJS.Workbook()
        const sheet = workbook.addWorksheet(t('REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH'))

        const titleRow = sheet.addRow([t('REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH')])
        titleRow.font = { bold: true, size: 16 }
        sheet.mergeCells(titleRow.number, 1, titleRow.number, 20)
        titleRow.alignment = { horizontal: 'left' }

        // Filter info (horizontal)
        const filterLabels = [
            t('REPORT.FILTER.BRANCH'),
            t('REPORT.FILTER.RECEIPT_TYPE'),
            t('REPORT.FILTER.OPERATIVE_TYPE'),
            t('REPORT.FILTER.OPERATIVE'),
            t('REPORT.FILTER.DATE_START'),
            t('REPORT.FILTER.DATE_END'),
        ]
        const filterValues = [
            branchId === 0 ? t('REPORT.FILTER.ALL') : _.get(_.find(branches, { branchId }), 'branchName', ''),
            filter.billStatus === 'ALL' ? t('REPORT.FILTER.ALL') : t(`REPORT.FILTER.BILL.${filter.billStatus}`),
            filter.operativeTypeName || t('REPORT.FILTER.ALL'),
            filter.operativeName || t('REPORT.FILTER.ALL'),
            filterDateStart ? toBuddhistYear(moment(filterDateStart), 'DD/MM/YYYY') : '-',
            filterDateEnd ? toBuddhistYear(moment(filterDateEnd), 'DD/MM/YYYY') : '-',
        ]

        const filterRowData: any[] = []
        for (let i = 0; i < filterLabels.length; i++) {
            filterRowData.push(filterLabels[i] + ':', filterValues[i])
        }
        const filterRow = sheet.addRow(filterRowData)
        filterRow.font = { size: 11 }
        for (let col = 1; col <= filterRowData.length; col += 2) {
            filterRow.getCell(col).font = { bold: true }
            filterRow.getCell(col).alignment = { horizontal: 'left' }
            filterRow.getCell(col + 1).alignment = { horizontal: 'left' }
        }
        sheet.addRow([])

        const headCells = [
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.#NO'), width: 10 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DATE'), width: 20 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.CN_NUMBER'), width: 15 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.BRANCH_CN_NUMBER'), width: 15 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.FULL_NAME'), width: 25 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.OPERATIVE_TYPE'), width: 20 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.LIST'), width: 25 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.PRICE'), width: 15 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.QTY'), width: 10 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.DISCOUNT'), width: 15 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.ADDITIONAL'), width: 15 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PRICE'), width: 15 },
            { label: t('REPORT.TABLE.OPERATIVE_SUMMARY_MONTH.TOTAL_PAID'), width: 15 },
        ]

        sheet.columns = headCells.map(c => ({ width: c.width }))

        const headerRow = sheet.addRow(headCells.map(c => c.label))
        headerRow.font = { bold: true, size: 12 }
        headerRow.alignment = { horizontal: 'left', vertical: 'middle' }

        for (const row of data) {
            const rowData = [
                data.indexOf(row) + 1,
                toBuddhistYear(moment(row.createdAt), 'DD/MM/YYYY HH:mm น.'),
                row.cnNumber || '-',
                row.branchCnNumber || '-',
                row.fullName || '-',
                row.operativeTypeName || '-',
                row.operativeName || '-',
                numberFormat(row.price),
                row.qty || '-',
                numberFormat(row.discount),
                numberFormat(row.additional),
                numberFormat(row.total),
                numberFormat(row.amountPaid),
            ]
            sheet.addRow(rowData)
        }

        // Summary row
        const summaryRow = sheet.addRow([
            '', '', '', '', '', '',
            t('REPORT.TITLE.SUM'),
            numberFormat(_.sumBy(data, 'price')),
            numberFormat(_.sumBy(data, 'qty'), 0),
            numberFormat(_.sumBy(data, 'discount')),
            numberFormat(_.sumBy(data, 'additional')),
            numberFormat(_.sumBy(data, 'total')),
            numberFormat(_.sumBy(data, 'amountPaid')),
        ])
        summaryRow.font = { bold: true }

        forceTextOnDateCells(workbook)
        const buffer = await workbook.xlsx.writeBuffer()
        const timestamp = moment().format('YYYYMMDD_HHmmss')
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, `${t('REPORT.REPORT_NAME.OPERATIVE_SUMMARY_MONTH')}_${timestamp}.xlsx`)
    } catch (error) {
        console.error('Error exporting to Excel:', error);
    }
}

export default ExportToExcel 